import React, {useEffect, useState} from 'react';
import {StyleSheet, RefreshControl, Platform} from 'react-native';
import {Box, Heading, VStack, HStack, ScrollView} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  BalanceIcon,
  AppendIcon,
  ClientsIcon,
  SearchCircleIcon,
  ScheduleIcon,
} from '../../../../components/icons';
import {useSalesmanContext} from '../../../../context/salesman';
import useToast from '../../../../hooks';
import DashboardTemplate from '../../../../components/templates/dashboard';
import {
  DashboardItem,
  SummaryAccountItem,
} from '../../../../components/dashboard';
import {StackParamList} from '../../../../navigators';
import {useContextState as userContext} from '../../../../context/user';
import {SpinnerModal} from '../../../../components/modals';
import {message as t} from '../../../../utils/messages';
import * as Keychain from '../../../../utils/keychain';
let ReactNativeForegroundService;
if (Platform.OS === 'android') {
  ReactNativeForegroundService =
    require('@supersami/rn-foreground-service').default;
} else {
  ReactNativeForegroundService = null;
}

type ScreenProps = NativeStackScreenProps<StackParamList, 'SalesmanDashboard'>;

export const DashboardScreen = ({navigation}: ScreenProps) => {
  const {
    signOff,
    notifications,
    fetchNotificationList,
    goToMessages,
    setGoToMessages,
    profile,
    setLocalizationActive,
    isLocalizationActive,
    saveState: saveUserSession,
    clearStorage: clearUserSession,
  } = userContext();

  const {
    fetchBalanceSheet,
    balanceSheet,
    fetchResources,
    setSalesmanClient: setClient,
    setSalesmanOrderFilter: setFilter,
    setPortfolioFilter,
    retrieveRequestToServer,
    requestsToSync,
  } = useSalesmanContext();

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const {showServerError} = useToast();

  const getNotifications = async () => {
    if (await fetchNotificationList()) {
      return;
    }
    throw new Error('Internal error');
  };

  const getBalanceSheet = async () => {
    if (await fetchBalanceSheet()) {
      return;
    }
    throw new Error('Internal error');
  };

  const getData = async () => {
    try {
      await getNotifications();
      await getBalanceSheet();
    } catch (ex) {
      showServerError();
    }
  };

  const getResources = async () => {
    try {
      setIsLoading(true);

      const result = await fetchResources();
      if (result) {
        await getData();
      } else {
        showServerError();
      }
      await getData();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const loadRequestSaved = async () => {
    await retrieveRequestToServer();
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestsToSync]);

  useEffect(() => {
    if (goToMessages) {
      setGoToMessages(false);
      handleTapNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToMessages]);

  useEffect(() => {
    if (balanceSheet) {
      return;
    }
    getResources();
    loadRequestSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = async () => {
    setRefreshing(true);
    setIsLoading(true);
    await getData();
    setIsLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const handleShowBalance = () => {
    setPortfolioFilter(null);
    navigation.navigate('SalesmanPortfolioDashboard');
  };

  const handleTapNotifications = () => {
    navigation.navigate('Notifications');
  };

  const handleShowProfile = () => {
    navigation.navigate('Profile');
  };

  const handleLogOut = () => {
    setLocalizationActive(false);
    clearUserSession();
    signOff();
    Keychain.resetGenericPassword();
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return 'test';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleTapProdile = () => {
    handleShowProfile();
  };

  const handleGoToSchedule = () => {
    navigation.navigate('SalesmanScheduleList');
  };

  const handleShowClientList = () => {
    navigation.navigate('SalesmanClientList');
  };

  const handleShowOrderList = () => {
    setClient(null);
    setFilter(null);
    navigation.navigate('SalesmanOrderList');
  };

  const handleAddClient = () => {
    navigation.navigate('AddClientStep1Screen');
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  const handleOnTapLocalization = async () => {
    if (profile?.referenceId) {
      const isActive = !isLocalizationActive;
      setLocalizationActive(isActive);
      if (isActive) {
        saveUserSession();
      } else {
        clearUserSession();
        if (Platform.OS === 'android' && ReactNativeForegroundService) {
          ReactNativeForegroundService.stopAll();
          ReactNativeForegroundService.remove_all_tasks();
        }
      }
    }
  };

  const hasItemsForSynchronizate = () => {
    if (requestsToSync) {
      return (
        requestsToSync?.customerRegistryList.length > 0 ||
        requestsToSync?.invoiceList.length > 0 ||
        requestsToSync?.paymentReportList.length > 0 ||
        requestsToSync?.salesOrderList.length > 0
      );
    }
    return false;
  };

  const handleOnTapSynchronization = () => {
    navigation.navigate('WithoutConnectionRequestList');
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      notifications={notifications ? notifications : []}
      onTapNotifications={() => handleTapNotifications()}
      onTapLogout={() => handleLogOut()}
      onTapProfile={() => handleTapProdile()}
      onTapLocalization={() => handleOnTapLocalization()}
      isActiveLocalization={isLocalizationActive ?? false}
      hasLocalization={true}
      canCallCenter={true}
      hasSynchronization={hasItemsForSynchronizate()}
      onTapSynchronization={handleOnTapSynchronization}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={balanceSheet && balanceSheet ? t('updating') : undefined}
        />
        <Box borderRadius="md" marginTop={22}>
          <VStack space={3} justifyContent="center" marginX={4}>
            <DashboardItem
              label="salesman-dashboard-balances-title"
              icon={<BalanceIcon width={40} style={styles.balanceIcon} />}
              onPress={() => handleShowBalance()}
              shadow={true}>
              <SummaryAccountItem
                localBalance={!isLoading ? balanceSheet?.local : '---'}
                foreignBalance={!isLoading ? balanceSheet?.foreign : '---'}
              />
            </DashboardItem>

            <DashboardItem
              paddingTop={4}
              paddingBottom={2}
              title={
                <Heading
                  color={'#06355E'}
                  size="sm"
                  fontWeight="extrabold"
                  fontStyle="italic"
                  textAlign="left"
                  marginTop={3}>
                  {t('salesman-dashboard-item-order-list').toUpperCase()}{' '}
                </Heading>
              }
              icon={<SearchCircleIcon width={40} />}
              onPress={() => handleShowOrderList()}
            />

            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('salesman-dashboard-item-clients').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={<ClientsIcon width={40} />}
              onPress={() => handleShowClientList()}
            />
            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('salesman-dashboard-item-schedule').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={<ScheduleIcon width={40} style={styles.scheduleIcon} />}
              onPress={() => handleGoToSchedule()}
            />
            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('salesman-dashboard-item-clients-new').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={<AppendIcon width={40} />}
              onPress={() => handleAddClient()}
            />
          </VStack>
        </Box>
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
  scheduleIcon: {
    marginTop: 10,
  },
});
